import { useQueryClient } from "react-query";
import { SectionBox } from "@foodpilot/foods";
import { ActivitiesHeader } from "./ActivitiesData/ActivitiesHeader";
import { Statistic, ActivityDataStatistics } from "../../../components/ActivityDataGrid/ActivityDataStatistics";
import { useTranslation } from "react-i18next";
import { Fragment, useEffect, useState } from "react";
import { ActivitiesService } from "./ActivitiesData/ActivitiesService";
import { ActivitiesFormData, ActivitiesFormIngredientSupplier } from "./ActivitiesData/Activities.type";
import { SuppliersList } from "./ActivitiesData/SuppliersList";
import { Box, Typography } from "@mui/material";
import { useIngredientContext } from "./useIngredientContext";
import { useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { useUpsertIngredientSupplierActivityData } from "src/api/client-api/ingredientSupplierActivityData/useUpsertIngredientSupplierActivityData";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
import { getIngredientScoresKey } from "src/api/client-api/olapScores/useGetIngredientScores";
import { getOneIngredientKey } from "src/api/client-api/ingredients/useGetOneIngredient";
import { ingredientGridKey } from "src/api/client-api/ingredients/useGetAllIngredients";

export const IngredientActivitiesData = () => {
    const [toggleEditMode, setToggleEditMode] = useState(false);
    const { ingredient } = useIngredientContext();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { currentCampaign } = useFoodpilotContext();

    const [prevActivities, setPrevActivities] = useState<ActivitiesFormData>({ suppliers: [] });
    const [activities, setActivities] = useState<ActivitiesFormData>({ suppliers: [] });
    const [areActivitiesSaving, setActivitiesSaving] = useState<boolean>(false);

    useEffect(() => {
        const activitiesData = ActivitiesService.getIngredientToActivities(ingredient, currentCampaign);

        setPrevActivities(structuredClone(activitiesData));
        setActivities(activitiesData);
    }, [ingredient, currentCampaign]);

    const statistics = useComputeStatistics(activities.suppliers);

    const updateActivityData = useUpsertIngredientSupplierActivityData({ ingredientId: ingredient?.id });

    if (ingredient === undefined || currentCampaign === null) return;

    return (
        <Fragment>
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_ACTIVITY_HEADER} />
            <Typography variant="h2">
                {t("donnees_d_activite_des_fournisseurs_de_l_ingredient")} - {currentCampaign.label}
            </Typography>
            <SectionBox
                header={
                    <ActivitiesHeader
                        ingredient={ingredient}
                        activities={activities}
                        toggleEditMode={toggleEditMode}
                        onActionClick={async () => {
                            const newValue = !toggleEditMode;

                            if (!newValue) {
                                setActivitiesSaving(true);

                                const activityDatas = ActivitiesService.getIngredientSupplierModifiedActivityDatas(
                                    prevActivities.suppliers,
                                    activities.suppliers,
                                    currentCampaign,
                                );

                                try {
                                    await Promise.all(
                                        activityDatas.map((activityData) =>
                                            updateActivityData.mutateAsync(activityData),
                                        ),
                                    );

                                    setActivitiesSaving(false);
                                    setToggleEditMode(false);
                                    setPrevActivities(structuredClone(activities));

                                    queryClient.invalidateQueries([ingredientGridKey]);
                                    queryClient.invalidateQueries([getOneIngredientKey, ingredient.id.toString()]);
                                    queryClient.invalidateQueries([getIngredientScoresKey, ingredient.id]);
                                } catch (error) {
                                    setActivitiesSaving(false);
                                    console.error("Failed to save activity data:", error);
                                }

                                return;
                            }

                            setToggleEditMode(true);
                        }}
                        areActivitiesSaving={areActivitiesSaving}
                    />
                }
            >
                <Box sx={{ display: "flex", gap: "2rem", flexDirection: "column" }}>
                    <ActivityDataStatistics statistics={statistics} />
                    <SuppliersList
                        activities={activities}
                        toggleEditMode={toggleEditMode}
                        onActivitiesUpdate={setActivities}
                    />
                </Box>
            </SectionBox>
            <ExtensionPoint extensionPoint={EXTENSION_POINTS.INGREDIENT_DETAIL_ACTIVITY_FOOTER} />
        </Fragment>
    );
};

type ComputeStatistics = {
    totalGrossSales: number;
    totalQuantity: number;
};

const useComputeStatistics = (ingredientSupplier: ActivitiesFormIngredientSupplier[]) => {
    const { t } = useTranslation();

    const computedStatistics = ingredientSupplier.reduce<ComputeStatistics>(
        (acc, ingredientSupplier) => {
            return (ingredientSupplier.ingredientSupplierActivityDatas ?? []).reduce<ComputeStatistics>(
                (acc, activityData) => {
                    acc.totalGrossSales += activityData.grossSales || 0;
                    acc.totalQuantity += activityData.quantity || 0;

                    return acc;
                },
                acc,
            );
        },
        { totalGrossSales: 0, totalQuantity: 0 },
    );

    const averageTotalPrice =
        computedStatistics.totalQuantity <= 0 ?
            0
        :   computedStatistics.totalGrossSales / computedStatistics.totalQuantity;

    const statitics: Statistic[] = [
        { title: t("ingredient.activityform.quantitytotal"), value: computedStatistics.totalQuantity, unit: "T" },
        { title: t("ingredient.activityform.grosssalestotal"), value: computedStatistics.totalGrossSales, unit: "€" },
        {
            title: t("ingredient.activityform.averagepricetotal"),
            value: averageTotalPrice,
            unit: "€/T",
        },
    ];

    return statitics;
};
